import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

/**
 * BasicButtonComponent
 *
 * Purpose
 * - Reusable button component based on Figma designs
 * - Supports text input via @Input text property
 * - Supports multiple sizes and states
 * - Content can be provided via input property or projected templates
 *
 * Visual states
 * - default: Primary purple background with box-shadow
 * - hover: Darker purple background (#462853)
 * - focus: Blue border (#0B1231) with glow effect
 * - disabled: Grayed out appearance (opacity: 0.4), no interaction
 *
 * Sizes
 * - large: Height 40px, padding 0 16px, font-size 14px (default)
 * - medium: Standard size for most use cases
 * - small: Compact size for dense UIs
 *
 * Features
 * - Text can be passed via @Input text property
 * - Supports projected content via templates
 * - Keyboard accessible (Enter/Space)
 * - Click event handling with disabled state protection
 *
 * Inputs
 * - text: string (optional) - Button text. If omitted, provide content via template
 * - size: 'large' | 'medium' | 'small' (default: 'large')
 * - type: 'primary' | 'secondary' | 'tertiary' | 'link' | 'ghost' | 'accept' | 'decline' (default: 'primary')
 * - rounded: boolean (default: false) - Rounds the button corners
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - isIcon: boolean (default: false) - Indicates if the button is an icon button
 * - dropShadow: boolean (default: true) - Applies drop shadow to the button. If false, removes box-shadow
 * - fontSize: string (optional) - Custom font size. If provided, overrides the default font size for the button size
 * - disableHoverEffect: boolean (default: false) - Disables hover effects on the button when set to true
 *
 * Outputs
 * - click: MouseEvent - Emitted on click when not disabled
 *
 * Templates (projected content)
 * - Default content slot: Any content between opening and closing tags
 *
 * Usage examples
 *
 * <!-- Primary button (default) -->
 * <app-basic-button text="Button" size="large" type="primary" (click)="onClick()"></app-basic-button>
 *
 * <!-- Secondary button -->
 * <app-basic-button text="Button" size="medium" type="secondary" (click)="onClick()"></app-basic-button>
 *
 * <!-- Tertiary button -->
 * <app-basic-button text="Button" type="tertiary" (click)="onClick()"></app-basic-button>
 *
 * <!-- Link button -->
 * <app-basic-button text="Link" type="link" (click)="onClick()"></app-basic-button>
 *
 * <!-- Ghost button -->
 * <app-basic-button text="Button" type="ghost" (click)="onClick()"></app-basic-button>
 *
 * <!-- Accept button -->
 * <app-basic-button text="Accept" type="accept" (click)="onClick()"></app-basic-button>
 *
 * <!-- Decline button -->
 * <app-basic-button text="Decline" type="decline" (click)="onClick()"></app-basic-button>
 *
 * <!-- Disabled state -->
 * <app-basic-button text="Disabled" [disabled]="true"></app-basic-button>
 *
 */


type ButtonSize = 'large' | 'medium' | 'small';
type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'link' | 'ghost' | 'accept' | 'decline';

@Component({
    selector: 'app-basic-button',
    templateUrl: './basic-button.component.html',
    styleUrls: ['./basic-button.component.scss'],
    standalone: false
})
export class BasicButtonComponent {
  @Input() text: string = '';
  @Input() size: ButtonSize = 'large';
  @Input() type: ButtonType = 'primary';
  @Input() disabled: boolean = false;
  @Input() rounded: boolean = false;
  @Input() isIcon: boolean = false;
  @Input() dropShadow: boolean = true;
  @Input() fontSize: string | null = null;
  @Input() disableHoverEffect: boolean = false;
  @Input() focusVisible: boolean = false;

  @Output() click = new EventEmitter<MouseEvent>();

  @HostBinding('class.disabled') get isDisabledHost(): boolean { return this.disabled; }
  @HostBinding('class.no-drop-shadow') get hasNoDropShadow(): boolean { return !this.dropShadow; }
  @HostBinding('class.no-hover-effect') get hasNoHoverEffect(): boolean { return this.disableHoverEffect; }

  onClick(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    event.stopPropagation();
    this.click.emit(event);
  }
}

